/**
 * useSelectedProperty
 *
 * Shared "selected property" hook for the Intelligence Engines.
 *
 * As of the ReSimpli unification pass this hook is a thin adapter over
 * the global ActivePropertyContext — so ANY of these paths keep the
 * whole workspace in sync:
 *   • Top-bar Active Property Pill
 *   • Pipeline card → engine deep-link (?property_id=)
 *   • Property Command Center intel tabs
 *   • Sidebar Intelligence Engine nav clicks (auto-append ?property_id=)
 *
 * Contract preserved for existing callers:
 *   const { propertyId, setPropertyId, selected } = useSelectedProperty(properties);
 *
 * `properties` is now optional — passed in it still seeds the fallback
 * selection but the global context is authoritative.
 */
import { useEffect } from "react";
import { useActiveProperty } from "@/context/ActivePropertyContext";

export function useSelectedProperty(properties = []) {
  const {
    propertyId, setPropertyId, selected,
    properties: ctxProperties,
  } = useActiveProperty();

  // If a caller passed us its own properties list (existing engine pages
  // load their own /v3/properties for their picker), and the global
  // context still doesn't have a valid selection, seed it from the caller.
  useEffect(() => {
    if (!properties?.length) return;
    const knownGlobal = new Set((ctxProperties || []).map((p) => p.id));
    if (!propertyId || !knownGlobal.has(propertyId)) {
      const local = new Set(properties.map((p) => p.id));
      if (propertyId && local.has(propertyId)) return; // already fine
      const fallback = properties[0]?.id;
      if (fallback && fallback !== propertyId) {
        setPropertyId(fallback);
      }
    }
    // Re-seed only when the passed properties list identity changes.
  }, [properties]);

  // Resolve `selected` against the caller's list first (they may have
  // fresher data than the global cache), then fall back to global.
  const localSelected = properties?.find?.((p) => p.id === propertyId) || null;
  return {
    propertyId,
    setPropertyId,
    selected: localSelected || selected || null,
  };
}

export default useSelectedProperty;
